/**
 * ReportFundsBeforeCDD.jsx: BM-H3355 (1 Oct 2026).
 *
 * AML Policy POL-01 v5 §7.14: money received for anything other than fees or
 * disbursements before CDD is passed must be reported to the MLRO at once.
 * CaseMinder does not see such money arrive, so the fee earner reports it
 * here. The server raises an MLRO alert with the High-Risk Notification Form
 * drafted. Server: POST /api/compliance/mlro-alerts/funds-before-cdd.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

export default function ReportFundsBeforeCDD({ matterRef }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", received_from: "", received_on: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!matterRef) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.amount || Number(form.amount) <= 0 || !form.received_from.trim() || !form.received_on) {
      setError("Give the amount, who it came from and the date it arrived.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/compliance/mlro-alerts/funds-before-cdd`,
        { matter_reference: matterRef, ...form, amount: Number(form.amount) }, { headers: authHeaders() });
      setResult(r.data);
    } catch (err) {
      setError(errorMessage(err, "The report could not be sent to the MLRO"));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="text-xs text-emerald-300" data-testid="funds-before-cdd-sent">
        Reported to the MLRO{result.cdd?.cdd_passed === false ? " (CDD is not yet passed on this matter)" : ""}.
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="funds-before-cdd-open"
        className="text-xs text-status-amber/80 hover:text-status-amber underline">
        Report money received before CDD to the MLRO
      </button>
    );
  }
  const input = "w-full px-2 py-1 text-xs rounded border border-navy-border bg-[rgba(26,37,71,0.85)] text-white";
  return (
    <form onSubmit={submit} className="space-y-2 p-3 rounded-md border border-amber-500/40" data-testid="funds-before-cdd-form">
      <p className="text-xs text-slate-300">
        AML Policy §7.14: money received for anything other than fees or disbursements before CDD is passed must be
        reported to the MLRO at once.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input id="fbc-amount" className={input} type="number" min="0" step="0.01" placeholder="Amount (£)"
          value={form.amount} onChange={set("amount")} />
        <input id="fbc-from" className={input} placeholder="Received from" value={form.received_from}
          onChange={set("received_from")} />
        <input id="fbc-on" className={input} type="date" value={form.received_on} onChange={set("received_on")} />
      </div>
      <textarea id="fbc-notes" className={input} rows={2} placeholder="Anything else the MLRO should know"
        value={form.notes} onChange={set("notes")} />
      {error ? <p className="text-xs text-red-300" data-testid="funds-before-cdd-error">{error}</p> : null}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="funds-before-cdd-submit"
          className="px-2 py-1 text-xs rounded bg-amber-600 text-white disabled:opacity-60">
          {busy ? "Sending…" : "Report to the MLRO"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="px-2 py-1 text-xs text-slate-400">Cancel</button>
      </div>
    </form>
  );
}
